import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Settings2, Trash2, Lock, Car, X, CheckSquare } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { api } from "../lib/api";
import { getVehicleTypes, registerVehicleTypes } from "../lib/vehicles";

export const useVehicleTypes = () => {
  const [all, setAll] = useState(getVehicleTypes());
  const [custom, setCustom] = useState([]);
  const reload = useCallback(() => api.vehicleTypes().then((d) => {
    registerVehicleTypes(d.all || []); setAll(getVehicleTypes()); setCustom(d.custom || []); return d;
  }).catch(() => {}), []);
  useEffect(() => { reload(); }, [reload]);
  return { all, custom, reload };
};

export const VehicleTypesManager = ({ onChanged }) => {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState("");
  const { all, custom, reload } = useVehicleTypes();
  const customNames = new Set(custom.map((c) => c.name));
  const add = async () => {
    if (!name.trim()) return;
    setBusy("add");
    try { await api.addVehicleType(name.trim()); toast.success("تمت إضافة النوع"); setName(""); await reload(); onChanged?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "تعذّرت الإضافة"); } finally { setBusy(""); }
  };
  const del = async (c) => {
    if (!window.confirm(`حذف النوع «${c.name}»؟`)) return;
    setBusy(c.id);
    try { await api.deleteVehicleType(c.id); toast.success("تم حذف النوع"); await reload(); onChanged?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "تعذّر الحذف"); } finally { setBusy(""); }
  };
  return (
    <>
      <Button size="sm" variant="outline" className="gap-1.5" data-testid="vehicle-types-manage-btn" onClick={() => setOpen(true)}>
        <Settings2 className="h-4 w-4" /> أنواع المركبات
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md font-body" dir="rtl" data-testid="vehicle-types-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Car className="h-5 w-5 text-blue-600" /> أنواع المركبات</DialogTitle>
            <DialogDescription>الأنواع الأساسية ثابتة، وتقدر تضيف أنواع خاصة بشركتك.</DialogDescription>
          </DialogHeader>
          <div className="flex gap-2">
            <Input value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} placeholder="مثال: باص، شاحنة صغيرة" data-testid="vehicle-type-new-input" />
            <Button onClick={add} disabled={!!busy || !name.trim()} className="gap-1 bg-blue-600 hover:bg-blue-700" data-testid="vehicle-type-add-btn">
              {busy === "add" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} إضافة
            </Button>
          </div>
          <div className="flex flex-wrap gap-2" data-testid="vehicle-types-list">
            {all.map((n) => {
              const c = custom.find((x) => x.name === n);
              return (
                <span key={n} data-testid={`vehicle-type-chip-${n}`} className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-sm ${customNames.has(n) ? "border-blue-200 bg-blue-50 text-blue-800" : "border-slate-200 bg-slate-50 text-slate-700"}`}>
                  {n}
                  {c ? <button onClick={() => del(c)} disabled={!!busy} className="text-rose-500 hover:text-rose-700" data-testid={`vehicle-type-del-${n}`}>{busy === c.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}</button>
                    : <Lock className="h-3 w-3 text-slate-400" />}
                </span>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

const TypeSelect = ({ value, onChange, testid }) => {
  const { all } = useVehicleTypes();
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="h-8 rounded-md border border-slate-200 bg-white px-2 text-xs min-w-36">
      <option value="">اختر نوع المركبة</option>
      {all.map((t) => <option key={t} value={t}>{t}</option>)}
    </select>
  );
};

export const BulkVehicleType = ({ selected, onDone }) => {
  const [v, setV] = useState(""), [busy, setBusy] = useState(false);
  if (!selected?.size) return null;
  const apply = async () => {
    if (!v) return toast.error("اختر نوع المركبة");
    setBusy(true);
    try { const r = await api.bulkEmployeeVehicleType([...selected], v); toast.success(`تم تغيير نوع المركبة لـ ${r.updated} مندوب${r.vehicles ? ` و${r.vehicles} مركبة مسلّمة لهم` : ""}`); setV(""); onDone && onDone(); }
    catch (e) { toast.error(e?.response?.data?.detail || "تعذّر التغيير"); } finally { setBusy(false); }
  };
  return (
    <div className="flex items-center gap-1" data-testid="bulk-emp-vehicle-type">
      <TypeSelect value={v} onChange={setV} testid="bulk-emp-vehicle-type-select" />
      <Button type="button" size="sm" className="h-8 bg-sky-600 hover:bg-sky-700" disabled={busy} onClick={apply} data-testid="bulk-emp-vehicle-type-apply">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "تغيير نوع المركبة"}
      </Button>
    </div>
  );
};

export const VehicleBulkBar = ({ selected, setSelected, visibleIds, onDone }) => {
  const [v, setV] = useState(""), [busy, setBusy] = useState(false);
  const vis = new Set(visibleIds);
  const ids = [...selected].filter((id) => vis.has(id));
  const apply = async () => {
    if (!v) return toast.error("اختر نوع المركبة");
    setBusy(true);
    try { const r = await api.bulkAssetVehicleType(ids, v); toast.success(`تم تغيير نوع ${r.updated} مركبة إلى «${v}»`); setSelected(new Set()); setV(""); onDone(); }
    catch (e) { toast.error(e?.response?.data?.detail || "تعذّر التغيير"); } finally { setBusy(false); }
  };
  return (
    <div data-testid="vehicles-bulk-bar" className="flex flex-wrap items-center gap-2 rounded-2xl border border-sky-100 bg-sky-50/50 p-2">
      <Button size="sm" variant="outline" className="h-8 gap-1.5 text-xs bg-white" data-testid="vehicles-select-all" onClick={() => setSelected(ids.length === visibleIds.length && ids.length ? new Set() : new Set(visibleIds))}>
        <CheckSquare className="h-3.5 w-3.5" /> {ids.length === visibleIds.length && ids.length ? "إلغاء التحديد" : `تحديد الكل (${visibleIds.length})`}
      </Button>
      <span className="text-sm font-semibold text-sky-800" data-testid="vehicles-selected-count">المحدد: {ids.length}</span>
      <TypeSelect value={v} onChange={setV} testid="vehicles-bulk-type-select" />
      <Button size="sm" className="h-8 bg-sky-600 hover:bg-sky-700" disabled={busy || !ids.length} onClick={apply} data-testid="vehicles-bulk-type-apply">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : `تغيير النوع للمحدد (${ids.length})`}
      </Button>
      {ids.length > 0 && <button className="text-slate-400 hover:text-slate-600" onClick={() => setSelected(new Set())} data-testid="vehicles-bulk-clear"><X className="h-4 w-4" /></button>}
    </div>
  );
};
